/* LumaSpec case studies (CASES lane): index filters, comparison sliders, image lightbox, showcase carousel.
   Plain custom elements, no dependencies; every element works without this file (cards show, chips are links,
   the carousel scrolls natively). Loaded as a module by the ls-case* / ls-cases-* sections that need it. */

/* Interface strings: snippets/ls-cases-i18n.liquid (locales ls.cases.js; placeholders {shown} / {total} in single braces
   so Liquid's t filter leaves them for this script), English defaults otherwise. */
const STR = { status_all: '{total} case studies', status_some: '{shown} of {total} case studies' };
try {
  Object.assign(STR, JSON.parse(document.querySelector('[data-ls-cases-i18n]')?.textContent || '{}'));
} catch {
  /* keep the defaults */
}

/* the t filter HTML-escapes plain (non-_html) keys: back to plain text once (shown via textContent / esc()) */
for (const k of Object.keys(STR)) {
  if (typeof STR[k] === 'string' && STR[k].includes('&')) {
    const d = document.createElement('textarea');
    d.innerHTML = STR[k];
    STR[k] = d.value;
  }
}

const norm = (s) => (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

class LsCaseFinder extends HTMLElement {
  connectedCallback() {
    if (this._ac) return;
    this._ac = new AbortController();
    const sig = { signal: this._ac.signal };
    this.cards = [...this.querySelectorAll('[data-case-card]')];
    this.chips = [...this.querySelectorAll('[data-filter]')];
    this.search = this.querySelector('[data-case-search]');
    this.status = this.querySelector('[data-case-status]');
    this.empty = this.querySelector('[data-case-empty]');
    this.reset = this.querySelector('[data-case-reset]');
    const p = new URLSearchParams(location.search);
    this.state = { industry: p.get('industry') || '', task: p.get('task') || '', q: p.get('q') || '' };
    if (this.search) this.search.value = this.state.q;
    for (const chip of this.chips) {
      chip.setAttribute('role', 'button');
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        const f = chip.dataset.filter;
        this.state[f] = this.state[f] === chip.dataset.value ? '' : chip.dataset.value;
        this.apply(true);
      }, sig);
    }
    let t;
    this.search?.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => { this.state.q = this.search.value.trim(); this.apply(true); }, 120);
    }, sig);
    this.reset?.addEventListener('click', () => {
      this.state = { industry: '', task: '', q: '' };
      if (this.search) this.search.value = '';
      this.apply(true);
    }, sig);
    // Industry shortcuts in the hub above: filter in place instead of reloading.
    for (const a of document.querySelectorAll('[data-industry-link]')) {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this.state.industry = a.dataset.industryLink;
        this.apply(true);
        this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, sig);
    }
    this.apply(false);
  }

  disconnectedCallback() { this._ac?.abort(); this._ac = null; }

  match(card, s, skip) {
    if (skip !== 'industry' && s.industry && card.dataset.industry !== s.industry) return false;
    if (skip !== 'task' && s.task && card.dataset.task !== s.task) return false;
    if (s.q) {
      const hay = norm(card.dataset.search);
      return norm(s.q).split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
    }
    return true;
  }

  apply(push) {
    const s = this.state;
    let shown = 0;
    for (const c of this.cards) {
      const ok = this.match(c, s);
      c.hidden = !ok;
      if (ok) shown += 1;
    }
    // Counts: what each chip would show combined with the other filter and the search.
    for (const chip of this.chips) {
      const f = chip.dataset.filter, v = chip.dataset.value;
      const n = this.cards.filter((c) => this.match(c, s, f) && (!v || c.dataset[f] === v)).length;
      const out = chip.querySelector('[data-count]');
      if (out) out.textContent = n;
      chip.setAttribute('aria-pressed', String(s[f] === v));
      chip.classList.toggle('is-zero', n === 0 && s[f] !== v);
    }
    const total = this.cards.length;
    if (this.status) this.status.textContent = (shown === total ? STR.status_all : STR.status_some).replace('{shown}', shown).replace('{total}', total);
    if (this.empty) this.empty.hidden = shown > 0;
    if (this.reset) this.reset.hidden = !(s.industry || s.task || s.q);
    if (push) {
      const u = new URL(location.href);
      for (const k of ['industry', 'task', 'q']) s[k] ? u.searchParams.set(k, s[k]) : u.searchParams.delete(k);
      history.replaceState(null, '', u);
    }
  }
}

class LsCaseGallery extends HTMLElement {
  connectedCallback() {
    if (this._ac) return;
    this._ac = new AbortController();
    const sig = { signal: this._ac.signal };
    for (const stage of this.querySelectorAll('[data-compare]')) {
      const r = stage.querySelector('input[type=range]');
      r?.addEventListener('input', () => stage.style.setProperty('--pos', `${r.value}%`), sig);
    }
    const dlg = this.querySelector('[data-lightbox]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const img = dlg.querySelector('[data-lightbox-img]');
    const cap = dlg.querySelector('[data-lightbox-cap]');
    let opener = null;
    for (const b of this.querySelectorAll('[data-zoom]')) {
      b.addEventListener('click', () => {
        opener = b;
        const thumb = b.querySelector('img');
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.src = b.dataset.zoom;
        img.alt = b.dataset.zoomAlt || '';
        cap.textContent = b.dataset.zoomAlt || '';
        if (thumb?.naturalWidth) { img.width = thumb.naturalWidth; img.height = thumb.naturalHeight; }
        dlg.showModal();
      }, sig);
    }
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); }, sig);
    dlg.addEventListener('close', () => opener?.focus(), sig);
  }

  disconnectedCallback() { this._ac?.abort(); this._ac = null; }
}

class LsCaseCarousel extends HTMLElement {
  connectedCallback() {
    if (this._ac) return;
    this._ac = new AbortController();
    const sig = { signal: this._ac.signal };
    const track = this.querySelector('[data-track]');
    const nav = this.querySelector('[data-nav]');
    if (!track || !nav) return;
    const prev = nav.querySelector('[data-prev]'), next = nav.querySelector('[data-next]');
    const step = () => {
      const card = track.firstElementChild;
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || '0') : track.clientWidth;
    };
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const go = (dir) => track.scrollBy({ left: dir * rtl * step(), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => go(-1), sig);
    next.addEventListener('click', () => go(1), sig);
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      const x = Math.abs(track.scrollLeft);
      nav.hidden = max <= 0;
      prev.disabled = x <= 2;
      next.disabled = x >= max;
    };
    track.addEventListener('scroll', update, { passive: true, signal: this._ac.signal });
    this._ro = new ResizeObserver(update);
    this._ro.observe(track);
    update();
  }

  disconnectedCallback() { this._ac?.abort(); this._ac = null; this._ro?.disconnect(); }
}

if (!customElements.get('ls-case-finder')) customElements.define('ls-case-finder', LsCaseFinder);
if (!customElements.get('ls-case-gallery')) customElements.define('ls-case-gallery', LsCaseGallery);
if (!customElements.get('ls-case-carousel')) customElements.define('ls-case-carousel', LsCaseCarousel);
